import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';

import Circular from 'components/common/CustomCircular';
import { LanguageChip } from 'components/training';
import {
  LearnQuestionSummary,
  QuestionSourceType,
  listLearnAssignment,
  listLearnQuestions,
} from 'stores/TrainingQuestionStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import LearnBreadcrumbs from './components/LearnBreadcrumbs';

/**
 * 課題内の設問一覧（受講・API6352）。
 * 各設問に自分の言語別クリア状況チップを表示する。
 * API6352 は課題名を返さないため、割当課題一覧（API6351）から課題名を解決して表示する。
 */
const QuestionList: React.FC = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [questions, setQuestions] = React.useState<LearnQuestionSummary[]>([]);
  const [assignmentName, setAssignmentName] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!assignmentId) return;
    setLoading(true);
    // 設問一覧（API6352）と、課題名解決用の割当課題一覧（API6351）を並行取得する
    Promise.all([listLearnQuestions(assignmentId), listLearnAssignment().catch(() => [])])
      .then(([qs, assignments]) => {
        setQuestions(qs);
        const found = assignments.find((a) => a.assignmentId === assignmentId);
        setAssignmentName(found?.name ?? '');
      })
      .catch(() => errorDefault('設問一覧の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [assignmentId]);

  const clearedCount = questions.filter((q) => q.cleared).length;
  const summaryPct =
    questions.length === 0 ? 0 : Math.round((clearedCount / questions.length) * 100);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Circular open={loading} />
      <LearnBreadcrumbs
        items={[
          { label: '研修', to: '/training-learn' },
          { label: '課題', to: '/training-learn/learn-assignment' },
          { label: assignmentName || '設問一覧' },
        ]}
      />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
        {assignmentName || '設問一覧'}
      </Typography>
      {assignmentName && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          設問一覧
        </Typography>
      )}
      {!assignmentName && <Box sx={{ mb: 1.5 }} />}

      {/* 進捗サマリ（設問クリア状況） */}
      {!loading && questions.length > 0 && (
        <Box
          sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 2, maxWidth: 720 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              クリア状況
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {clearedCount} / {questions.length} 問クリア（{summaryPct}%）
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={summaryPct}
            color={summaryPct === 100 ? 'success' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {!loading && questions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          この課題には設問がありません
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
          {questions.map((q) => (
            <Paper
              key={q.questionId}
              elevation={0}
              onClick={() =>
                navigate(
                  `/training-learn/learn-assignment/${assignmentId}/question/${q.questionId}`,
                )
              }
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {q.cleared && <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />}
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                  {q.title}
                </Typography>
                <Chip
                  size="small"
                  label={QuestionSourceType[q.sourceType]}
                  variant="outlined"
                  sx={{ borderRadius: 1, height: 20, fontSize: 11 }}
                />
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {q.languages.map((lang) => (
                  <LanguageChip
                    key={lang.extension}
                    extension={lang.extension}
                    cleared={lang.cleared}
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default QuestionList;
